import ExportMenu from "@/components/collections/ExportMenu";
import useCustomSearchFilters from "@/hooks/useCustomSearchFilters";
import {
  ClassicSearchResponse,
  SearchResponseItem,
} from "@/lib/SemanticScholarApi";
import { customSearch } from "@/lib/cloudFunctions/customSearch";
import Container from "@/ui/Container";
import Pagination from "@/ui/Pagination";
import SearchArticle from "@/ui/SearchArticle";
import SearchLoading from "@/ui/pending/SearchLoading";
import { ArrowBackIcon } from "@chakra-ui/icons";

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IoArrowUp } from "react-icons/io5";

import { useNavigate, useNavigation, useSearchParams } from "react-router";

const cache: {
  key: string;
  [key: number]: ClassicSearchResponse | undefined;
} = {
  key: "",
};

type FormDataType = {
  all?: string;
  title?: string;
  abstract?: string;
  authors?: Array<string>;
  publication_venues?: Array<string>;
  publication_types?: Array<string>;
  fields_of_study?: Array<string>;
  min_citations?: number;
  min_year?: number;
  max_year?: number;
  is_open_access?: boolean;
};

const CustomSearchResults = () => {
  const [params] = useSearchParams();
  const {
    title,
    authors,
    publicationVenues,
    publicationTypes,
    fieldsOfStudy,
    minCitations,
    minYear,
    maxYear,
    isOpenAccess,
    setFormData,
  } = useCustomSearchFilters();
  const page = parseInt(params.get("page") || "1", 10);
  const navigate = useNavigate();
  const navigation = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<ClassicSearchResponse>();
  const { t } = useTranslation();
  const busy =
    navigation.state === "loading" &&
    navigation.location.pathname === "/search";

  const [shouldDisplayScrollToTop, setShouldDisplayScrollToTop] =
    useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, busy]);

  useEffect(() => {
    const displayScrollToTop = () => {
      if (window.scrollY > 100) {
        setShouldDisplayScrollToTop(true);
      } else {
        setShouldDisplayScrollToTop(false);
      }
    };

    window.addEventListener("scroll", displayScrollToTop, { passive: true });

    return () => window.removeEventListener("scroll", displayScrollToTop);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const key = params.toString().replace(/page=\d+&?/, "");

    if (page in cache && cache.key === key) {
      setResults(cache[page]);
      setIsLoading(false);
      return;
    }

    //Set the cache key
    cache.key = params.toString().replace(/page=\d+&?/, "");

    // Create an object from the params
    const queryParams = Object.fromEntries(params.entries());

    const customSearchData: FormDataType = {};

    if (queryParams.min_year) {
      customSearchData.min_year = parseInt(queryParams.min_year);
      setFormData({
        minYear: parseInt(queryParams.min_year),
      });
    }
    if (queryParams.max_year) {
      customSearchData.max_year = parseInt(queryParams.max_year);
      setFormData({
        maxYear: parseInt(queryParams.max_year),
      });
    }
    if (queryParams.min_citations) {
      customSearchData.min_citations = parseInt(queryParams.min_citations);
      setFormData({
        minCitations: parseInt(queryParams.min_citations),
      });
    }

    if (queryParams.title) {
      customSearchData.title = queryParams.title;
      setFormData({
        title: queryParams.title,
      });
    }

    if (queryParams.authors) {
      customSearchData.authors = queryParams.authors.split(",");
      setFormData({
        authors: queryParams.authors.split(","),
      });
    }
    if (queryParams.publication_venues) {
      customSearchData.publication_venues =
        queryParams.publication_venues.split(",");
    }
    if (queryParams.publication_types) {
      customSearchData.publication_types =
        queryParams.publication_types.split(",");
    }
    if (queryParams.fields_of_study) {
      customSearchData.fields_of_study = queryParams.fields_of_study.split(",");
    }
    if (queryParams.is_open_access) {
      customSearchData.is_open_access = queryParams.is_open_access === "true";
      setFormData({
        isOpenAccess: queryParams.is_open_access === "true",
      });
    }
    customSearch({
      data: customSearchData,
      page: queryParams.page ? parseInt(queryParams.page) : 1,
    })
      .then((response) => {
        const responseData = response.data as {
          papers: Array<SearchResponseItem>;
          total: number;
        }; // Explicitly type response.data

        setResults({
          data: responseData.papers,
          total: responseData.total,
          isAiSearch: false,
        });
        // Cache the results
        cache[page] = {
          data: responseData.papers,
          total: responseData.total,
          isAiSearch: false,
        };
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching search results:", error);
        setIsLoading(false);
      });
  }, [params, setFormData, page]);

  return (
    <Container>
      <Box
        mt={8}
        p={[4, 8]}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        gap={4}
        w={"100%"}
        h={"100%"}
        borderRadius={24}
        bgColor={"#F2F6F9"}
      >
        <>
          {" "}
          <HStack justifyContent={"flex-start"} w={"100%"}>
            <Button
              alignSelf={"flex-start"}
              variant={"primary"}
              w={"fit-content"}
              onClick={() => navigate("/search/custom")}
              mr={"auto"}
            >
              <Box
                borderWidth={1}
                borderColor={"#FFF"}
                borderRadius={100}
                w={6}
                h={6}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                mr={2}
              >
                <ArrowBackIcon />
              </Box>
              {t("article.back")}
            </Button>

            {results && results?.total > 0 && (
              <ExportMenu papers={results.data} title={"export"} />
            )}
          </HStack>
          {busy || isLoading ? (
            <SearchLoading />
          ) : (
            <>
              {results && results.total > 0 ? (
                <>
                  <Box
                    gap={8}
                    p={2}
                    display={"flex"}
                    flexDirection={"column"}
                    w={"100%"}
                  >
                    <Accordion allowToggle>
                      <AccordionItem>
                        <AccordionButton>
                          <Text fontWeight={600}>
                            {t("search.result.customsFilters")}
                          </Text>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                          {title && (
                            <Text>
                              <strong>{t("search.fields.title")}:</strong>{" "}
                              {title}
                            </Text>
                          )}
                          {authors && authors.length > 0 && (
                            <Text>
                              <strong> {t("search.fields.authors")}:</strong>{" "}
                              {authors.join(", ")}
                            </Text>
                          )}
                          {publicationVenues &&
                            publicationVenues.length > 0 && (
                              <Text>
                                <strong>
                                  {t("search.fields.publicationVenue")}:{" "}
                                </strong>
                                {publicationVenues
                                  .map((e) => e.label)
                                  .join(", ")}
                              </Text>
                            )}
                          {publicationTypes && publicationTypes.length > 0 && (
                            <Text>
                              <strong>
                                {t("search.filters.publicationTypes.title")}:{" "}
                              </strong>
                              {publicationTypes.map((e) => e.label).join(", ")}
                            </Text>
                          )}
                          {fieldsOfStudy && fieldsOfStudy.length > 0 && (
                            <Text>
                              <strong>
                                {t("search.fields.fieldsOfStudy")}:{" "}
                              </strong>
                              {fieldsOfStudy.map((e) => e.label).join(", ")}
                            </Text>
                          )}
                          {minCitations && (
                            <Text>
                              <strong>
                                {t("search.fields.minCitations")}:
                              </strong>{" "}
                              {minCitations}
                            </Text>
                          )}
                          {minYear && (
                            <Text>
                              <strong>{t("search.fields.minYear")}:</strong>{" "}
                              {minYear}
                            </Text>
                          )}
                          {maxYear && (
                            <Text>
                              <strong>{t("search.fields.maxYear")}:</strong>{" "}
                              {maxYear}
                            </Text>
                          )}
                          {isOpenAccess && (
                            <Text fontWeight={"bold"}>
                              {t("search.filters.isOpenAccess")}
                            </Text>
                          )}
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                    {results.data.map((document) => (
                      <SearchArticle
                        key={document.paperId}
                        {...document}
                        publicationTypes={document.publicationtypes}
                      />
                    ))}
                  </Box>
                  <Pagination
                    total={results.total > 1000 ? 1000 : results.total}
                    current={page}
                    onChange={(currentPage) => {
                      const queryParams = new URLSearchParams(
                        window.location.search
                      );

                      queryParams.set("page", currentPage.toString());
                      navigate({
                        search: queryParams.toString(),
                      });
                    }}
                    resultsPerPage={10}
                  />
                </>
              ) : (
                <VStack gap={8} w={"100%"} p={2}>
                  <Text>{t("search.error")}</Text>
                </VStack>
              )}
            </>
          )}
        </>
      </Box>
      <IconButton
        icon={<Icon as={IoArrowUp} w={6} h={6} />}
        aria-label="Scroll to top"
        onClick={() =>
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          })
        }
        display={shouldDisplayScrollToTop ? "block" : "none"}
        position={"fixed"}
        bottom={8}
        right={8}
      />
    </Container>
  );
};

export default CustomSearchResults;
